import { useFormContext } from 'react-hook-form';
import { useMemo } from 'react';
import { DateIntervalFields, SelectField } from '@mews-commander/form';
import { LocalizedSubsectionTitle } from '@mews-commander/ui';
import {
    DateTimeService,
    DateTimeUtils,
    FormCounterField,
    FormSelectionCardGroupField,
    lt,
    useAppClockService,
} from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Container, Divider, Stack } from '@optimus-web/core';
import { GroupOption, Option, WithEnterpriseId } from '@mews-commander/types';
import { getEnterpriseTimeZoneIdentifier } from '@mews-commander/legacy-entities/src/ducks/enterprises';
import { useCommanderSelector } from '@mews-commander/utils';
import { getDayOptions } from '@mews-commander/core/legacy-globalization';
import { SellLimitFieldNames } from '../fieldNames';
import { MAX_SELL_LIMIT_SPACES, AddSellLimitFormModel, MIN_SELL_LIMIT_SPACES } from '../../schemas';

interface Props extends WithEnterpriseId {
    groupedRateOptions: GroupOption[];
    rateGroupOptions: Option[];
    resourceCategoryOptions: Option[];
}

export const AddSellLimitFormFields = ({
    enterpriseId,
    groupedRateOptions,
    rateGroupOptions,
    resourceCategoryOptions,
}: Props) => {
    const dayOptions = useCommanderSelector((state) => getDayOptions(state));
    const { watch } = useFormContext<AddSellLimitFormModel>();

    const { type: rateOptionType } = watch(SellLimitFieldNames.RATE_SELECT_OPTION_FIELD_NAME);
    const { type: spaceOptionType } = watch(SellLimitFieldNames.SPACE_SELECT_OPTION_FIELD_NAME);

    const timezone = useCommanderSelector((state) => getEnterpriseTimeZoneIdentifier(state, { enterpriseId }));
    const appClockService = useAppClockService();
    const minDate = useMemo(() => {
        const currentDateTime = DateTimeService.fromUtc(appClockService.getAppNowUtc(), timezone);
        return DateTimeUtils.dateTimeToStructuredDate(currentDateTime);
    }, [timezone, appClockService]);

    const rateSelectOptionContentMap = {
        exactRate: (
            <SelectField
                key={SellLimitFieldNames.EXACT_RATE_IDS_FIELD_NAME}
                label={lt('SellLimitsFormSelectRatesLabel')}
                name={`${SellLimitFieldNames.RATE_SELECT_OPTION_FIELD_NAME}.${SellLimitFieldNames.EXACT_RATE_IDS_FIELD_NAME}`}
                multiselect
                required
                options={groupedRateOptions}
                size="medium"
                helper={lt('SellLimitsFormSelectRatesCaption')}
            />
        ),
        rateGroup: (
            <SelectField
                key={SellLimitFieldNames.RATE_GROUP_IDS_FIELD_NAME}
                label={lt('SelectRateGroupPlural')}
                helper={lt('SellLimitsFormSelectRateGroupsCaption')}
                name={`${SellLimitFieldNames.RATE_SELECT_OPTION_FIELD_NAME}.${SellLimitFieldNames.RATE_GROUP_IDS_FIELD_NAME}`}
                multiselect
                required
                options={rateGroupOptions}
                size="medium"
            />
        ),
    };

    const spaceSelectOptionContentMap = {
        allSpaces: null,
        resourceCategory: (
            <SelectField
                label={lt('SelectSpaceCategoryPlural')}
                helper={lt('SellLimitsFormSelectSpacesCaption')}
                name={`${SellLimitFieldNames.SPACE_SELECT_OPTION_FIELD_NAME}.${SellLimitFieldNames.RESOURCE_CATEGORY_IDS_FIELD_NAME}`}
                multiselect
                required
                options={resourceCategoryOptions}
                size="medium"
            />
        ),
    };

    return (
        <Stack vertical spacing="300">
            <Stack vertical spacing="100">
                <LocalizedSubsectionTitle textKey={TextKey.SellLimitsFormDefineSectionTitle} />
            </Stack>
            <FormCounterField
                name={SellLimitFieldNames.NUMBER_OF_SPACES_FIELD_NAME}
                label={lt('SellLimitsFormMaxSpaceLabel')}
                helper={lt('SellLimitsFormMaxSpaceCaption')}
                min={MIN_SELL_LIMIT_SPACES}
                max={MAX_SELL_LIMIT_SPACES}
                required
                size="medium"
            />
            <DateIntervalFields
                inline
                mode="utc"
                size="medium"
                startDateProps={{
                    name: SellLimitFieldNames.LIMIT_START_FIELD_NAME,
                    label: lt('StartDate'),
                    required: true,
                }}
                endDateProps={{
                    name: SellLimitFieldNames.LIMIT_END_FIELD_NAME,
                    label: lt('EndDate'),
                    required: true,
                }}
                minDate={minDate}
            />
            <SelectField
                name={SellLimitFieldNames.DAYS_FIELD_NAME}
                multiselect
                options={dayOptions}
                size="medium"
                label={lt('SellLimitsFormDaysTitle')}
                helper={lt('SellLimitsFormDaysCaption')}
                required
            />

            <Container padding={['100', '0']}>
                <Divider />
            </Container>

            <LocalizedSubsectionTitle textKey={TextKey.SellLimitsRateSelectOptionTitle} />
            <FormSelectionCardGroupField
                type="radio"
                options={rateSelectOptions}
                name={`${SellLimitFieldNames.RATE_SELECT_OPTION_FIELD_NAME}.type`}
            />
            {rateSelectOptionContentMap[rateOptionType]}
            <Container padding={['100', '0']}>
                <Divider />
            </Container>

            <LocalizedSubsectionTitle textKey={TextKey.SellLimitsSpaceSelectOptionTitle} />
            <FormSelectionCardGroupField
                type="radio"
                options={spaceSelectOptions}
                name={`${SellLimitFieldNames.SPACE_SELECT_OPTION_FIELD_NAME}.type`}
            />
            {spaceSelectOptionContentMap[spaceOptionType]}
        </Stack>
    );
};

const rateSelectOptions = [
    {
        value: 'exactRate',
        name: 'exactRate',
        label: lt('ExactRate'),
    },
    {
        value: 'rateGroup',
        name: 'rateGroup',
        label: lt('RateGroup'),
    },
];
const spaceSelectOptions = [
    {
        value: 'allSpaces',
        name: 'allSpaces',
        label: lt('AllSpaces'),
    },
    {
        value: 'resourceCategory',
        name: 'resourceCategory',
        label: lt('SpaceCategory'),
    },
];
